import { useState, useEffect, useMemo } from 'react';
import { useTransactions } from '@/hooks/useTransactions';
import { useCustomersSaas } from '@/hooks/useCustomersSaas';
import { useProducts } from '@/hooks/useProducts';
import { useSubscriptionLimits } from '@/hooks/useSubscriptionLimits';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Search, ArrowUpDown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PlanLimitTooltip } from '@/components/ui/plan-limit-tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TransactionDialog } from '@/components/transactions/TransactionDialog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function Transactions() {
  const { transactions, isLoading } = useTransactions();
  const { customers } = useCustomersSaas();
  const { products } = useProducts();
  const { manualEntryEnabled, plan } = useSubscriptionLimits();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [productFilter, setProductFilter] = useState<string>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // Agrupar clientes por nome para evitar duplicação no filtro
  const uniqueCustomers = useMemo(() => {
    if (!customers) return [];
    
    const customerMap = new Map<string, any>();
    
    customers.forEach(customer => {
      // Usar nome como chave (primeiro registro ganha)
      if (!customerMap.has(customer.name)) {
        customerMap.set(customer.name, customer);
      }
    });
    
    return Array.from(customerMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );
  }, [customers]);

  useEffect(() => {
    document.title = 'Dashi | Transações';
  }, []);

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    
    return sortConfig.direction === 'asc' 
      ? <ArrowUpDown className="h-4 w-4 ml-1 transform rotate-180" />
      : <ArrowUpDown className="h-4 w-4 ml-1" />;
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      paid: 'default',
      pending: 'secondary',
      refunded: 'destructive',
      failed: 'outline',
    };
    
    const labels: Record<string, string> = {
      paid: 'Pago',
      pending: 'Pendente',
      refunded: 'Estornado',
      failed: 'Falhou',
    };

    return (
      <Badge variant={variants[status] || 'outline'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getSourceBadge = (source: string) => {
    const labels: Record<string, string> = {
      api: 'API',
      spreadsheet: 'Planilha',
      manual: 'Manual',
    };

    return (
      <Badge variant="outline">
        {labels[source] || source}
      </Badge>
    );
  };

  const filteredTransactions = transactions?.filter((transaction: any) => {
    const matchesSearch = 
      transaction.customer_saas?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      transaction.customer_saas?.product?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || transaction.payment_status === statusFilter;
    const matchesProduct = productFilter === 'all' || transaction.customer_saas?.product?.id === productFilter;
    const matchesCustomer = customerFilter === 'all' || transaction.customer_saas?.name === customerFilter;
    
    return matchesSearch && matchesStatus && matchesProduct && matchesCustomer;
  });

  const sortedTransactions = filteredTransactions?.sort((a: any, b: any) => {
    if (!sortConfig) return 0;
    
    let aValue: any;
    let bValue: any;
    
    switch (sortConfig.key) {
      case 'date':
        aValue = new Date(a.payment_date).getTime();
        bValue = new Date(b.payment_date).getTime();
        break;
      case 'customer':
        aValue = a.customer_saas?.name || '';
        bValue = b.customer_saas?.name || '';
        break;
      case 'product':
        aValue = a.customer_saas?.product?.name || '';
        bValue = b.customer_saas?.product?.name || '';
        break;
      case 'plan':
        aValue = a.product_plan?.name || '';
        bValue = b.product_plan?.name || '';
        break;
      case 'amount':
        aValue = a.amount_paid || 0;
        bValue = b.amount_paid || 0;
        break;
      case 'status':
        aValue = a.payment_status || '';
        bValue = b.payment_status || '';
        break;
      case 'source':
        aValue = a.data_source || '';
        bValue = b.data_source || '';
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleEdit = (transaction: any) => {
    setSelectedTransaction(transaction);
    setDialogOpen(true);
  };

  const handleNew = () => {
    setSelectedTransaction(null);
    setDialogOpen(true);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ArrowUpDown className="h-8 w-8" />
            Movimentações
          </h1>
          <p className="text-muted-foreground mt-1">
            Acompanhe pagamentos e transações
          </p>
        </div>
        <PlanLimitTooltip
          enabled={manualEntryEnabled}
          planName={plan?.name || 'atual'}
          limitType="feature"
          featureName="Lançamento Manual"
        >
          <Button onClick={handleNew} disabled={!manualEntryEnabled}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Transação
          </Button>
        </PlanLimitTooltip>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
          <CardDescription>Refine sua busca</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por cliente ou produto..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="paid">Pago</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="refunded">Estornado</SelectItem>
                <SelectItem value="failed">Falhou</SelectItem>
              </SelectContent>
            </Select>
            <Select value={productFilter} onValueChange={setProductFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Produto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os produtos</SelectItem>
                {products?.map((product) => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os clientes</SelectItem>
                {uniqueCustomers?.map((customer) => (
                  <SelectItem key={customer.id} value={customer.name}>
                    {customer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Transações</CardTitle>
          <CardDescription>
            {filteredTransactions?.length || 0} transação(ões) encontrada(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('date')}
                    >
                      <div className="flex items-center">
                        Data
                        {getSortIcon('date')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('customer')}
                    >
                      <div className="flex items-center">
                        Cliente
                        {getSortIcon('customer')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('product')}
                    >
                      <div className="flex items-center">
                        Produto
                        {getSortIcon('product')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('plan')}
                    >
                      <div className="flex items-center">
                        Plano
                        {getSortIcon('plan')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('amount')}
                    >
                      <div className="flex items-center">
                        Valor
                        {getSortIcon('amount')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => handleSort('source')}
                    >
                      <div className="flex items-center">
                        Origem
                        {getSortIcon('source')}
                      </div>
                    </TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTransactions?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8">
                        Nenhuma transação encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedTransactions?.map((transaction: any) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {format(new Date(transaction.payment_date), 'dd/MM/yyyy', { locale: ptBR })}
                        </TableCell>
                        <TableCell className="font-medium">
                          {transaction.customer_saas?.name || '-'}
                        </TableCell>
                        <TableCell>
                          {transaction.customer_saas?.product?.name || '-'}
                        </TableCell>
                        <TableCell>
                          {transaction.product_plan?.name || '-'}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(transaction.amount_paid)}
                        </TableCell>
                        <TableCell>{getStatusBadge(transaction.payment_status)}</TableCell>
                        <TableCell>{getSourceBadge(transaction.data_source)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(transaction)}
                          >
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <TransactionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        transaction={selectedTransaction}
      />
    </div>
  );
}
